import type { Expression, Map as MapboxMap } from 'mapbox-gl';
import type { TileLayerConfig } from '../types';

/** Prefix of every Map Query dataset layer id. */
export const DATASET_LAYER_PREFIX = 'dq-';

/** A filter no feature matches: the layer stays mounted but paints nothing. */
export const MATCH_NONE: Expression = ['==', ['literal', 0], ['literal', 1]];

/**
 * The style's layer ids, or null while the style itself is (re)loading, when
 * getStyle() throws. (Not isStyleLoaded(): that is also false while tiles load.)
 * @param {MapboxMap} map The Mapbox map.
 * @return {string[] | null} Layer ids, bottom first.
 */
export function styleLayerIds(map: MapboxMap): string[] | null {
  try {
    return (map.getStyle()?.layers ?? []).map((layer) => layer.id);
  } catch {
    return null;
  }
}

/**
 * Id of the layer DynamicTileLayer renders for one of a dataset's tileLayers, so
 * hover / hit-test code rebuilds the same id without duplicating the
 * `dq-${geoAttribute}-${idx}-${layerType}` string.
 * @param {TileLayerConfig} layer The tile layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The layer id.
 */
export const docLayerId = (layer: TileLayerConfig, idx: number): string =>
  `dq-${layer.geoAttribute ?? 'layer'}-${idx}-${layer.layerType}`;

/**
 * Id of the outline a fill layer with `outline` draws from its own source.
 * @param {TileLayerConfig} layer The fill layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The outline layer id.
 */
export const outlineLayerId = (layer: TileLayerConfig, idx: number): string =>
  `${DATASET_LAYER_PREFIX}${layer.geoAttribute ?? 'layer'}-${idx}-outline`;

/**
 * Id of the halo a `highlightInOwnColor` fill layer draws around its selected
 * feature, from the same source as its outline.
 * @param {TileLayerConfig} layer The fill layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The halo layer id.
 */
export const haloLayerId = (layer: TileLayerConfig, idx: number): string =>
  `${outlineLayerId(layer, idx)}-halo`;

/**
 * Id of the invisible aggs hit-target rendered for a `clusterCount` layer; the
 * hover popup queries it to read `_count` (documents stacked on the dot).
 * @param {TileLayerConfig} layer The clustered layer.
 * @param {number} idx Its index in the dataset's tileLayers.
 * @return {string} The hit-target layer id.
 */
export const clusterCountLayerId = (
  layer: TileLayerConfig,
  idx: number
): string => `dq-${layer.geoAttribute ?? 'layer'}-${idx}-count`;

export const buildTextFieldExpr = (fields: string[]): Expression => {
  if (fields.length === 1) return ['get', fields[0]] as Expression;
  const parts: unknown[] = [];
  fields.forEach((f, i) => {
    if (i > 0) parts.push(' ');
    parts.push(['get', f]);
  });
  return ['concat', ...parts] as Expression;
};
